import React from 'react';

import { Button } from '@opendoor/atoms/button';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

export type ICtaStyles = 'Primary' | 'Secondary' | 'Tertiary' | 'Text';

interface LPLinkProps {
  ctaStyle?: ICtaStyles;
  ctaUrl?: string;
  ctaDisplayText: string;
  className?: string;
  analyticsName?: string;
  'aria-label': string;
  tabIndex?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  // Matches the Atoms Button contract: an anchor when `ctaUrl` is set, otherwise
  // a <button> (sheet CTAs). The Text branch narrows this to the anchor Link.
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
}

interface LPFauxBtnProps {
  ctaStyle?: ICtaStyles;
  ctaDisplayText: string;
  className?: string;
}

// Atoms exposes primary / secondary (no "tertiary"); Tertiary maps to secondary.
// "Text" is rendered as an inline Link rather than a button.
const ctaStyleToVariant = (ctaStyle: ICtaStyles): 'primary' | 'secondary' =>
  ctaStyle === 'Primary' ? 'primary' : 'secondary';

/**
 * Landing-page CTA. Renders a real Atoms `Button` (anchor when `ctaUrl` is set,
 * otherwise a `<button>` for sheet/dialog CTAs) or, for the `Text` style, an
 * inline `Link`. Analytics (`linkClickEvent` / `buttonClickEvent`) fire through
 * Atoms' built-in `analyticsName` handling.
 */
export const LPLink = ({
  ctaStyle = 'Primary',
  ctaUrl,
  ctaDisplayText,
  className,
  analyticsName,
  'aria-label': ariaLabel,
  tabIndex,
  onKeyDown,
  onClick,
  ref,
}: LPLinkProps) => {
  if (ctaStyle === 'Text') {
    return (
      <Link
        href={ctaUrl}
        aria-label={ariaLabel}
        analyticsName={analyticsName}
        className={cn('w-max', className)}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the Text style always renders an anchor (Link), so the shared ref narrows to HTMLAnchorElement here
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {ctaDisplayText}
      </Link>
    );
  }

  const variant = ctaStyleToVariant(ctaStyle);

  return ctaUrl ? (
    <Button
      variant={variant}
      href={ctaUrl}
      aria-label={ariaLabel}
      analyticsName={analyticsName}
      className={className}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      ref={ref}
    >
      {ctaDisplayText}
    </Button>
  ) : (
    <Button
      variant={variant}
      aria-label={ariaLabel}
      analyticsName={analyticsName}
      className={className}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      ref={ref}
    >
      {ctaDisplayText}
    </Button>
  );
};

// Non-interactive faux button: it sits inside a non-clickable Multicard card, so
// it's a decorative label rather than its own interactive element. The styles are
// inlined (Tailwind) to mirror the button/link look without an interactive
// Button/Link.
const FAUX_VARIANT_CLASSES: Record<'primary' | 'secondary', string> = {
  primary: 'bg-od-bg-accent-brand text-od-content-inverse-primary',
  secondary: 'border border-od-border-primary bg-od-bg-primary text-od-content-primary',
};

/**
 * Non-interactive CTA that visually matches {@link LPLink} but renders inside an
 * already-clickable / non-interactive Multicard card, so it must not be its own
 * interactive element.
 */
export const LPFauxBtn = ({ ctaStyle = 'Primary', ctaDisplayText, className }: LPFauxBtnProps) => {
  if (ctaStyle === 'Text') {
    return (
      <Text
        as="span"
        className={cn(
          'inline-flex items-center text-label-lg text-od-content-accent-brand underline',
          className,
        )}
      >
        {ctaDisplayText}
      </Text>
    );
  }

  return (
    <Text
      as="span"
      className={cn(
        'inline-flex h-14 items-center justify-center rounded-2xl px-6 text-label-lg',
        FAUX_VARIANT_CLASSES[ctaStyleToVariant(ctaStyle)],
        className,
      )}
    >
      {ctaDisplayText}
    </Text>
  );
};
